import { makeAutoObservable } from 'mobx'
import type { Currency } from '@/shared/api/types'

export type Theme = 'light' | 'dark'
export type ToastKind = 'success' | 'error'

/**
 * Диалог кабинета, открытый поверх текущей страницы.
 *
 * Состояние, а не маршрут — намеренно. Раньше кошельки, автовыводы,
 * отправка и получение были адресами (/app/wallets и т.п.), и открытие
 * диалога означало смену страницы: обзор под ним перемонтировался,
 * прокрутка терялась, а сам диалог зависел от того, где на странице
 * человек его открыл. Диалог — это окно над тем, что человек видит,
 * а не другое место в приложении.
 */
export type CabinetDialog =
  | { kind: 'wallets' }
  | { kind: 'auto' }
  | { kind: 'send'; currency: Currency }
  | { kind: 'receive'; currency: Currency }

export interface Toast {
  id: number
  kind: ToastKind
  text: string
}

const THEME_KEY = 'cck-theme'
const TOAST_MS = 5000

/** Тема и всплывающие сообщения — всё, что не про данные. */
export class UiStore {
  theme: Theme = 'light'
  toasts: Toast[] = []
  dialog: CabinetDialog | null = null

  private nextId = 1
  private timers = new Map<number, number>()

  constructor() {
    makeAutoObservable<this, 'nextId' | 'timers'>(this, {
      nextId: false,
      timers: false,
    })
    this.theme = readTheme() ?? systemTheme()
    this.applyTheme()
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark'
    // Плавная смена цветов — только на время переключения и только тем,
    // кто не просил убрать движение: класс включает переход на всех
    // элементах (см. styles.css), держать его постоянно дорого.
    const root = document.documentElement
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    if (!still) {
      root.classList.add('theme-switching')
      window.setTimeout(() => root.classList.remove('theme-switching'), 260)
    }
    this.applyTheme()
  }

  private applyTheme(): void {
    document.documentElement.dataset['theme'] = this.theme
    try {
      window.localStorage.setItem(THEME_KEY, this.theme)
    } catch {
      // Хранилище запрещено — тема просто не переживёт перезагрузку.
    }
  }

  toast(kind: ToastKind, text: string): void {
    const id = this.nextId++
    this.toasts.push({ id, kind, text })
    const timer = window.setTimeout(() => this.dismiss(id), TOAST_MS)
    this.timers.set(id, timer)
  }

  openDialog(dialog: CabinetDialog): void { this.dialog = dialog }
  closeDialog(): void { this.dialog = null }

  success(text: string): void { this.toast('success', text) }
  failure(text: string): void { this.toast('error', text) }

  dismiss(id: number): void {
    this.toasts = this.toasts.filter((t) => t.id !== id)
    const timer = this.timers.get(id)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      this.timers.delete(id)
    }
  }
}

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readTheme(): Theme | null {
  // В приватном окне и при запрете хранилища обращение БРОСАЕТ,
  // а не возвращает пусто. Уронить на этом приложение было бы глупо.
  try {
    const value = window.localStorage.getItem(THEME_KEY)
    return value === 'dark' || value === 'light' ? value : null
  } catch {
    return null
  }
}
